import Bugsnag from '@bugsnag/js';

import { HTTP_STATUS } from 'constants/env';

import { getHttpStatus } from './apiError';
import { getInitailAccessToken } from './auth';

export const TOKEN_RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];
export const TOKEN_RETRY_VISIBLE_ATTEMPT = 2;

export const TOKEN_RETRY_PHASE = Object.freeze({
    IDLE: 'idle',
    QUIET: 'quiet',
    VISIBLE: 'visible',
});

const sleep = ms =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

export const isTokenRateLimited = error =>
    getHttpStatus(error) === HTTP_STATUS.TOO_MANY_REQUESTS;

const reportTokenRetryBackoff = ({ attempt, delayMs }) => {
    Bugsnag.leaveBreadcrumb('Token API exponential backoff', {
        attempt,
        delayMs,
    });
};

const reportTokenRetryExhausted = (error, attempt) => {
    Bugsnag.notify(error, event => {
        event.context = 'Token API 429 backoff exhausted';
        event.addMetadata('tokenRetry', {
            attempts: attempt,
            delaysMs: TOKEN_RETRY_DELAYS_MS,
        });
    });
};

export const fetchAccessTokenWithRetry = async setTokenRetryPhase => {
    let lastError;
    let didSucceed = false;
    const maxAttempt = TOKEN_RETRY_DELAYS_MS.length;

    try {
        for (let attempt = 0; attempt <= maxAttempt; attempt += 1) {
            if (attempt >= TOKEN_RETRY_VISIBLE_ATTEMPT) {
                setTokenRetryPhase(TOKEN_RETRY_PHASE.VISIBLE);
            }

            try {
                const response = await getInitailAccessToken();
                didSucceed = true;
                return response;
            } catch (error) {
                lastError = error;
                const isLastAttempt = attempt === maxAttempt;
                const canRetry = isTokenRateLimited(error) && !isLastAttempt;

                if (!canRetry) {
                    if (isLastAttempt && isTokenRateLimited(error)) {
                        reportTokenRetryExhausted(error, attempt + 1);
                    }
                    throw error;
                }

                const delayMs = TOKEN_RETRY_DELAYS_MS[attempt];
                reportTokenRetryBackoff({
                    attempt: attempt + 1,
                    delayMs,
                });

                if (attempt < TOKEN_RETRY_VISIBLE_ATTEMPT) {
                    setTokenRetryPhase(TOKEN_RETRY_PHASE.QUIET);
                }

                await sleep(delayMs);
            }
        }

        throw lastError;
    } finally {
        if (didSucceed || !isTokenRateLimited(lastError)) {
            setTokenRetryPhase(TOKEN_RETRY_PHASE.IDLE);
        } else {
            setTokenRetryPhase(TOKEN_RETRY_PHASE.QUIET);
        }
    }
};
